import { Core } from 'core/Core';
import { Module } from 'core/Module';
import { InstanceLoader } from 'core/loaders/InstanceLoader';
import { Events } from 'core/events/Events';
import { CoreEvents } from 'core/events/CoreEvents';
import { snakeToClassName } from 'utils/string';

enum ClearType {
	DESTROY,
	REMOVE
}


export class ModuleManager {
	private _modules: Module[] = []; //All active modules
	private _instanceLoader: InstanceLoader<Module>; //To load module instances saved to window scope
	private _purgeQueId: any;
	private _autoCreateQueId: any;

	public create(): void {}

	public get(id: string): Module {
		return this._getModuleById(id);
	}

	public getByTag(tag: string): Array<Module> {
		return this._getModulesByTag(tag);
	}

	public getByModuleName(name: string): Array<Module> {
		return this._getByModuleName(name);
	}

	public remove(id: string): void {
		this._clearById(id, ClearType.REMOVE);
	}

	public destroyByTag(tag: string): void {
		this._clearByTag(tag);
		this._purge();
	}

	public destroy(id: string): void {
		this._clearById(id, ClearType.DESTROY);
	}

	public DOMReady(): void {
		this._queAutoCreate();
	}

	public DOMUpdated(): void {
		this._quePurge();
		this._queAutoCreate();
	}

	public tick(): void {
		this._modules.forEach((moduleInstance: Module) => {
			if (moduleInstance.tick) {
				moduleInstance.tick();
			}
		});
	}

	public scroll(windowY: number): void {
		this._modules.forEach((moduleInstance: Module) => {
			if (moduleInstance.scroll) {
				moduleInstance.scroll(windowY);
			}
		});
	}

	public resize(sizes: any): void {
		this._modules.forEach((moduleInstance: Module) => {
			if (moduleInstance.resize) {
				moduleInstance.resize(sizes);
			}
		});
	}

	public createModulesInTarget($target: HTMLElement): void {
		this._createModulesInTarget($target);
	}

	//private
	//--------------------------------------------------
	private _getModuleById(id: string): Module {
		for (let i = 0; i < this._modules.length; i++) {
			if (this._modules[i].id === id) {
				return this._modules[i];
			}
		}
		return null;
	}

	private _getModulesByTag(tag: string): Array<Module> {
		return this._modules.filter((moduleItem) => moduleItem.tag === tag);
	}

	private _getByModuleName(name: string): Array<Module> {
		return this._modules.filter((moduleItem) => moduleItem.moduleName === name);
	}

	private _queAutoCreate(): void {
		Core.DOM.clearQue(this._autoCreateQueId);
		this._autoCreateQueId = Core.DOM.que(this._autoCreateModules.bind(this));
	}

	//Grab any module that haven't been created
	//Create modules are assigned the m-mounted="true" attribute and stop being selected again
	private _autoCreateModules(target: any = document): void {
		//Create a dyamic instance loader on the window.Core scope:
		if (!this._instanceLoader) {
			this._instanceLoader = new InstanceLoader<Module>((window as any).DebugbaseModules);
		}

		this._createModulesInTarget(document);

		Events.dispatch(CoreEvents.MODULES_MOUNTED);
	}

	private _createModulesInTarget(target: any): void {
		let modules: NodeListOf<Element> = target.querySelectorAll("[m]:not([m-mounted='true'])");

		modules.forEach((moduleElement: Element) => {
			try {
				this._createModule(moduleElement);
			} catch (e) {
				console.log('MODULE LOAD ERROR !!!!!!!!!!!!!!!!!!!!!!', moduleElement, e);
			}
		});
	}

	//Create a single module to push into the the
	//This is a special case (Like behaviours) where modules classes are set to set window.scope and create at runtime
	//So those classes need to be imported to app or relevant scope
	private _createModule(element: Element): void {
		//Get a list of attbuites parsed into numbers/booleans/strings:
		let attributes: any = Core.framework.getParsedAttributes(element);

		//Create the instance:
		let instance: Module = this._getInstance(attributes.m);

		instance.init();
		//Modules are mounted on [data-m] HTML elements, so the Element here is an HTMLElement:
		instance.$container = element as HTMLElement;
		instance.id = element.getAttribute('id');

		if (attributes['m-tag']) {
			instance.tag = attributes['m-tag'];
		}

		Core.framework.attributesToConfig(instance, attributes, attributes.m);
		instance.moduleName = attributes.m;

		instance.create();

		//Flag mounted:
		instance.mounted = true;
		Core.DOM.setAttribute(element, 'm-mounted', 'true');

		//Add to stack:
		this._modules.push(instance);
	}

	private _quePurge(): void {
		Core.DOM.clearQue(this._purgeQueId);
		this._purgeQueId = Core.DOM.que(this._purge.bind(this));
	}

	private _purge(): void {
		let i = 0;

		console.log('Destroy purche started', this._modules);

		while (i < this._modules.length) {
			//Check if module removed/missing but not destroyed:
			if (!this._modules[i].destroyed && this._isRemoved(this._modules[i])) {
				this._destroyModule(this._modules[i]);
			}

			if (this._modules[i].destroyed) {
				Events.dispatch(CoreEvents.MODULE_DESTROYED, this._modules[i].id);
				this._modules.splice(i, 1);
			} else {
				i++;
			}
		}

		console.log('Destroy purche ended', this._modules);
	}

	private _getInstance(moduleName: string): Module {
		let className: string = snakeToClassName(moduleName);
		return this._instanceLoader.getInstance(className);
	}

	private _isRemoved(moduleInstance: Module): boolean {
		return moduleInstance.removed || !Core.DOM.contains(moduleInstance.$container);
	}

	private _clearById(id: string, type: ClearType): void {
		let module = this._getModuleById(id);
		if (!module) {
			return;
		}
		//Remove OR destroy module:
		if (type === ClearType.DESTROY) {
			this._destroyModule(module);
		} else {
			this._removeModule(module);
		}

		//Remove (Que asnc so it only happens once no matter how much its called):
		this._quePurge();
	}

	private _clearByTag(tag: string): void {
		this._getModulesByTag(tag).forEach((item) => this._destroyModule(item));
	}

	private _removeModule(moduleInstance: Module): void {
		if (!moduleInstance.$container) {
			return;
		}

		moduleInstance.removed = true;
		Core.DOM.removeElement(moduleInstance.$container);
		Core.DOM.removeAttributte(moduleInstance.$container, 'm');
		Core.DOM.removeAttributte(moduleInstance.$container, 'm-mouted');
		moduleInstance.$container = null;

		if (!moduleInstance.destroyed) {
			this._destroyModule(moduleInstance);
		}
	}

	private _destroyModule(moduleInstance: Module): void {
		moduleInstance.destroy();
		moduleInstance.active = false;
		moduleInstance.$container = null;
		moduleInstance.destroyed = true;
	}
}

if ('NodeList' in window && !NodeList.prototype.forEach) {
	NodeList.prototype.forEach = function (callback, thisArg) {
		thisArg = thisArg || window;
		for (var i = 0; i < this.length; i++) {
			callback.call(thisArg, this[i], i, this);
		}
	};
}
